import * as Yup from 'yup';
import { isEmpty, omit, keys } from 'lodash';
import { assign, MachineConfig, matchesState } from 'xstate';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import moment from 'moment';
import { DEFAULT_ENTITY_NAME } from '../../constants/constants';
import { SponsoredMessageModel } from '@mm/state/model/SponsoredMessageModel';
import { AdSetBudgetType } from '@mm/state/interfaces/adAccounts';
import { MMTargeting } from '@mm/state/model/AdConfigModel';

export const MIN_BID_CAP = 100; // Amount is in cents;

export type PBContext = typeof promoBlasterContext;
export type FullPBContext = typeof fullPBContext;

export type PromoBlasterMachineEventSchema =
  | { type: 'SEND_CLICKED' }
  | { type: 'CANCEL_CLICKED' }
  | { type: 'CONFIRM_SENDING' }
  | { type: 'CANCEL_SENDING' }
  | { type: 'CONFIRM_PUBLISHED' }
  | {
      type: 'ENTITY_UPDATE';
      data: {
        updates: Partial<SponsoredMessageModel>;
        error?: keyof SponsoredMessageModel;
      };
    }
  | { type: 'CTX_UPDATE_FROM_COMPONENT'; data: any };

export const budgets = [
  { id: AdSetBudgetType.LIFETIME, name: 'Lifetime' },
  { id: AdSetBudgetType.DAILY, name: 'Daily' },
];

export const noneAudience = {
  id: -999,
  name: 'All page subscribers',
  contactsCount: 'all',
  subscribedContactsCount: 'all',
  sponsoredSubscribedContactsCount: 'all',
} as unknown as AudiencesModel;

const promoBlasterContext = {
  sponsoredMessage: undefined as SponsoredMessageModel | undefined,
  localUpdates: {} as Partial<SponsoredMessageModel>,
  updated: false,
  promoBlasterName: DEFAULT_ENTITY_NAME,
  chatbotId: undefined as number | undefined,
  textOnly: false,
  targeting: null as MMTargeting | null,
  imageUrl: null as string | null,
  imageSubTitle: '' as string | null,
  audiences: [] as number[],
  adAccount: undefined as string | undefined,
  imageTitle: 'Title' as string | null,
  audienceId: noneAudience.id,
  budgetId: AdSetBudgetType.LIFETIME,
  /** Amount is in cents */
  bidAmount: 10000,
  budgetValue: 3000,
  startTime: moment(),
  endTime: moment().add(1, 'week'),
  error: '',
};

const fullPBContext = {
  ...promoBlasterContext,
  localFieldsErrors: {} as Partial<Record<keyof PBContext, string[]>>,
  remoteFieldsErrors: {} as Partial<
    Record<keyof SponsoredMessageModel, string[]>
  >,
};

type DataEntryStatesSchema = {
  states: {
    ready: {};
    published: {};
    error: { states: { publishingNotAllowed: {}; invalidFields: {} } };
    confirmModal: {};
  };
};

export type PBStatesSchema = {
  states: {
    init: {};
    sponsoredMessageFetching: {};
    sponsoredMessageUpdating: {};
    dataEntry: DataEntryStatesSchema;
    afterPublish: {};
    sponsoredMessagePublishing: {};
    dashboardRedirect: {};
    error: {};
  };
};

const dataEntryStatesConfig: MachineConfig<
  FullPBContext,
  DataEntryStatesSchema,
  PromoBlasterMachineEventSchema
> = {
  initial: 'ready',
  states: {
    ready: {
      always: [
        {
          target: '#promoBlaster.dashboardRedirect',
          cond: ctx => isLegacy(ctx),
        },
        {
          target: 'published',
          cond: ctx => isPublished(ctx),
        },
        {
          target: 'error.invalidFields',
          cond: ctx => !isPublished(ctx) && hasInvalidFields(ctx),
        },
        {
          target: 'error.publishingNotAllowed',
          cond: ctx => !isPublished(ctx) && publishingNotAllowed(ctx),
        },
      ],
      on: { SEND_CLICKED: 'confirmModal' },
    },
    published: {
      on: {
        SEND_CLICKED: 'confirmModal',
      },
    },
    confirmModal: {
      invoke: {
        id: 'openPromoPublishModal',
        src: 'promoModalService',
      },
      on: {
        CONFIRM_SENDING: '#promoBlaster.sponsoredMessagePublishing',
        CANCEL_SENDING: 'ready',
      },
    },
    error: {
      states: {
        invalidFields: {},
        publishingNotAllowed: {},
      },
      always: [
        {
          target: 'ready',
          cond: ctx => canPublish(ctx),
        },
        {
          target: 'error.publishingNotAllowed',
          cond: (ctx, env, meta) =>
            !matchesState(
              meta.state.value,
              'dataEntry.error.publishingNotAllowed',
            ) &&
            allFieldsValid(ctx) &&
            publishingNotAllowed(ctx),
        },
        {
          target: 'error.invalidFields',
          cond: (ctx, env, meta) =>
            !matchesState(meta.state.value, 'dataEntry.error.invalidFields') &&
            hasInvalidFields(ctx) &&
            !publishingNotAllowed(ctx),
        },
      ],
    },
  },
};

export const PBMachineConfig: MachineConfig<
  FullPBContext,
  PBStatesSchema,
  PromoBlasterMachineEventSchema
> = {
  id: 'promoBlaster',
  initial: 'init',
  context: fullPBContext,
  states: {
    init: {
      always: [
        { target: 'sponsoredMessageFetching', cond: 'hasDataFromReduxStore' },
      ],
    },
    sponsoredMessageUpdating: {
      invoke: {
        id: 'updateSponsoredMessage',
        src: 'updateDataService',
        onDone: {
          target: 'dataEntry',
          actions: assign((ctx, evt) => {
            const {
              data: { error, key, updated },
            } = evt;
            let updates: Partial<FullPBContext> = {};
            if (updated) {
              updates = {
                sponsoredMessage: updated,
                ...(key
                  ? { remoteFieldsErrors: omit(ctx.remoteFieldsErrors, key) }
                  : {}),
              };
            } else if (key && error) {
              updates = {
                remoteFieldsErrors: {
                  ...ctx.remoteFieldsErrors,
                  [key]: error,
                },
              };
            }
            if (updates.remoteFieldsErrors) {
              updates.localFieldsErrors = fullErrors(
                validateLocalFields(ctx),
                updates.remoteFieldsErrors,
              );
            }
            return updates;
          }),
        },
        onError: {
          target: 'error',
          actions: assign((_, evt) => ({ error: evt.data })),
        },
      },
    },
    sponsoredMessageFetching: {
      invoke: {
        id: 'fetchSponsoredMessage',
        src: 'fetchDataService',
        onDone: {
          target: 'dataEntry',
          actions: assign((_, evt) => {
            const sponsoredMessage: SponsoredMessageModel = evt.data;
            return {
              sponsoredMessage,
              promoBlasterName: sponsoredMessage.name,
              textOnly: sponsoredMessage.textPageSelected,
              imageSubTitle: sponsoredMessage.subtitle,
              targeting: sponsoredMessage.targeting,
              audienceId:
                sponsoredMessage.audiences.length > 0
                  ? Number(sponsoredMessage.audiences[0])
                  : noneAudience.id,
              imageTitle: sponsoredMessage.title,
              budgetId: sponsoredMessage.adSetBudgetType,
              imageUrl: sponsoredMessage.imageUrl,
              /** Amount is in cents */
              bidAmount: sponsoredMessage.bidAmount,
              budgetValue: sponsoredMessage.adSetBudget,
              startTime: moment(sponsoredMessage.adSetStartDate),
              endTime: moment(sponsoredMessage.adSetEndDate),
              error: '',
            };
          }),
        },
        onError: {
          target: 'error',
          actions: assign((_, evt) => ({ error: evt.data })),
        },
      },
    },
    dataEntry: { ...dataEntryStatesConfig },
    sponsoredMessagePublishing: {
      invoke: {
        id: 'publishSponsoredMessage',
        src: 'publishSponsoredMessageService',
        onDone: {
          target: 'afterPublish',
        },
        onError: {
          target: 'dataEntry',
          actions: assign((_, evt) => ({ error: evt.data })),
        },
      },
    },
    afterPublish: {
      invoke: {
        id: 'openOnPublishModal',
        src: 'promoPublishModalService',
      },
      on: {
        CONFIRM_PUBLISHED: 'dashboardRedirect',
      },
    },
    dashboardRedirect: {
      type: 'final',
      onEntry: 'redirectToDashboardAction',
    },
    error: {
      type: 'final',
      onEntry: 'redirectToDashboardAction',
      // TODO: Come up with something talking to Dan,
      // but for now it's just 'bad final state'.
      // Users do get notified about failures by standard modals.
    },
  },
  on: {
    ENTITY_UPDATE: [
      {
        actions: assign((ctx, evt) => {
          const {
            data: { updates },
          } = evt;
          return {
            localUpdates: {
              ...ctx.localUpdates,
              ...updates,
            },
          };
        }),
        cond: (ctx, _evt, _meta) => isPublished(ctx),
      },
      {
        target: 'sponsoredMessageUpdating',
        cond: (ctx, _evt, _meta) => !isPublished(ctx),
      },
    ],
    CTX_UPDATE_FROM_COMPONENT: {
      actions: assign((ctx, evt) => {
        const updatedContext = {
          ...ctx,
          ...evt.data,
        };
        const localFieldsErrors = fullErrors(
          validateLocalFields(updatedContext),
          ctx.remoteFieldsErrors,
        );
        return {
          ...evt.data,
          localFieldsErrors,
        };
      }),
    },
    CANCEL_CLICKED: {
      target: 'dashboardRedirect',
    },
  },
};
const validateLocalFields = (ctx: PBContext) => {
  const errors: Partial<Record<keyof PBContext, string[]>> = {};
  if (invalidImageTitle(ctx)) {
    errors['imageTitle'] = ['Image title can not be blank'];
  }
  if (invalidBudget(ctx)) {
    errors['budgetValue'] = [
      'FB requires the budget to be at least $1 per day',
    ];
  }
  if (adAccountNotSelected(ctx)) {
    errors['adAccount'] = ['Ad account is required'];
  }
  if (invalidName(ctx)) {
    errors['promoBlasterName'] = [
      'Name should be between 1 and 100 characters long',
    ];
  }
  if (invalidBidCap(ctx)) {
    errors['bidAmount'] = ['FB requires the Bid Cap to be at least $25'];
  }
  return errors;
};
const fieldsMapping: Partial<
  Record<keyof PBContext, keyof SponsoredMessageModel>
> = {
  imageTitle: 'title',
  budgetValue: 'adSetBudget',
  adAccount: 'adAccountId',
  promoBlasterName: 'name',
  bidAmount: 'bidAmount',
};

const contextKeys = keys(promoBlasterContext);

const fullErrors = (
  computedErrors: Partial<Record<keyof PBContext, string[]>>,
  remoteFields: Partial<Record<keyof SponsoredMessageModel, string[]>>,
) =>
  contextKeys.reduce(
    (acc: Partial<Record<keyof PBContext, string[]>>, key: keyof PBContext) => {
      const mappedKey: keyof SponsoredMessageModel =
        fieldsMapping[key] || (key as keyof SponsoredMessageModel);
      if (!isEmpty(computedErrors[key]) || !isEmpty(remoteFields[mappedKey])) {
        return {
          ...acc,
          [key]: [
            ...(computedErrors[key] || []),
            ...(remoteFields[mappedKey] || []),
          ],
        };
      }
      return acc;
    },
    {},
  );

const allFieldsValid = (ctx: FullPBContext) => !hasInvalidFields(ctx);
const hasInvalidFields = (ctx: FullPBContext) =>
  !isEmpty(ctx.remoteFieldsErrors) || !isEmpty(ctx.localFieldsErrors);
const publishingNotAllowed = (ctx: FullPBContext) =>
  !(
    Boolean(ctx.sponsoredMessage?.adAccountId) &&
    Boolean(ctx.sponsoredMessage?.userConnected)
  ) && ctx.adAccount === undefined;

export const canPublish = (ctx: FullPBContext) =>
  !publishingNotAllowed(ctx) &&
  !hasInvalidFields(ctx) &&
  hasPublishingChanges(ctx);

export const hasPublishingChanges = (ctx: FullPBContext) => {
  const { sponsoredMessage, localUpdates } = ctx;
  if (isPublished(ctx) && sponsoredMessage) {
    if (ctx.updated) {
      return true;
    }
    const hasKey = keys(localUpdates).find(
      (k: keyof Partial<SponsoredMessageModel>) =>
        localUpdates[k] !== sponsoredMessage[k],
    );
    return Boolean(hasKey);
  }
  return true;
};

const isLegacy = (ctx: FullPBContext) => Boolean(ctx.sponsoredMessage?.legacy);
const isPublished = (ctx: FullPBContext) =>
  ctx.sponsoredMessage !== undefined && !ctx.sponsoredMessage.draft;

const promoBlasterNameSchema = Yup.string().trim().min(1).max(100);

const adAccountNotSelected = (ctx: PBContext) =>
  ctx.adAccount === undefined && !ctx.sponsoredMessage?.adAccountId;

const invalidImageTitle = (ctx: PBContext) =>
  ctx.imageUrl ? !ctx.imageTitle : false;

const invalidName = (ctx: PBContext) =>
  !promoBlasterNameSchema.isValidSync(ctx.promoBlasterName);

const invalidBudget = (ctx: PBContext) => {
  const { budgetId, budgetValue, startTime, endTime } = ctx;

  const fullDays = Math.ceil(endTime.diff(startTime, 'days', true));

  const isDailyBudget = budgetId === AdSetBudgetType.DAILY;

  // In cents.
  const minBudget = isDailyBudget ? 100 : fullDays * 100;

  return budgetValue < minBudget;
};

const invalidBidCap = ({ bidAmount }: PBContext) => bidAmount < MIN_BID_CAP; // Amount in cents.

export const hasDataFromReduxStore = (ctx: PBContext) => !!ctx.chatbotId;
